/**
 * @reference getServerSideSafeLanguage
 */
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import type { SupportedLanguage } from './types';

export const QUERY_LANGUAGE_KEY = ['queryLanguage'];

/**
 *
 * @param isHybrid - If true, the hook will first check the query cache for the language before falling back to the router locale. This is useful for pages that are a mix of OneLink and integrated pages, where the language may be set in the query cache for OneLink pages but not for integrated pages.
 * @returns
 */
export function useLanguage(isHybrid?: boolean): SupportedLanguage {
  const client = useQueryClient();
  const { locale } = useRouter();

  const localeFromReactQueryCache = client.getQueryData<SupportedLanguage>(QUERY_LANGUAGE_KEY);
  const supportedLocale = (!locale || locale === 'default' ? 'en' : locale) as SupportedLanguage;

  return isHybrid ? localeFromReactQueryCache || supportedLocale : supportedLocale;
}

/**
 * Hook to return the language set via the query cache or router. Calls useLanguage internally
 * Used when mixing OneLink pages with integrated pages.
 * @deprecated This hook is deprecated and should not be used in new code. Use `useLanguage` instead and set the query language using `setQueryLanguage` when necessary.
 */
export function useQueryLanguage() {
  return useLanguage(true);
}

export function setQueryLanguage(client: QueryClient, language: SupportedLanguage) {
  client.setQueryData(QUERY_LANGUAGE_KEY, language);
}
